import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { filterListByKeys, sortArray } from "../utilites/globalFunctions";

interface TableState {
  actualList: any;
  duplicateActualList: any;
  filteredList: any;
  sortState?: any;
  searchText?: string;
  rowsPerPage?: number;
  pageNumber?: number;
  totalPages?: number;
  sortOrder?: string;
  sortColumn?: string | null;
  isTableLoading?: boolean;
  columnNames?: string[];
}

const initialState: TableState = {
  actualList: [],
  duplicateActualList: [], // to revert the changes to the original list
  filteredList: [],
  sortState: {
    column: null,
    direction: "none",
  },
  rowsPerPage: 10,
  pageNumber: 1,
  searchText: "",
  totalPages: 1,
  sortOrder: "none",
  sortColumn: null,
  isTableLoading: false, // Added to manage loading state of the table
  columnNames: [],
};

const tableModalSlice = createSlice({
  name: "table",
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.actualList = [];
      state.duplicateActualList = [];
      state.filteredList = [];
      state.sortState = { column: null, direction: "none" };
      state.searchText = undefined;
    },
    setActualList: (state, action: PayloadAction<any[]>) => {
      state.actualList = action.payload;
      state.duplicateActualList = action.payload; // Store the original list for reference
      state.filteredList = action.payload; // Initialize filtered list with actual list
      state.totalPages = Math.ceil(
        action.payload.length / (state.rowsPerPage || 10),
      );
      state.filteredList = action.payload.slice(
        (1 - 1) * (state.rowsPerPage || 10),
        1 * (state.rowsPerPage || 10),
      );
    },
    setSortState: (
      state,
      action: PayloadAction<{
        column: string | null;
        direction: "asc" | "desc" | "none";
      }>,
    ) => {
      state.sortState = action.payload;
      state.pageNumber = 1;
      const _searchText = state.searchText?.toLowerCase() || "";
      let list: any[] = [];
      if (_searchText) {
        list = filterListByKeys(
          state.duplicateActualList,
          _searchText,
          state.columnNames || [],
        );
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        list = [...state.duplicateActualList]; // Reset to original list if search text is empty
      }
      list = sortArray(list, state.sortColumn || "", state.sortOrder || "none");
      state.totalPages = Math.ceil(list.length / (state.rowsPerPage || 10));
      state.actualList = list;
      state.filteredList = list.slice(
        (state.pageNumber - 1) * (state.rowsPerPage || 10),
        state.pageNumber * (state.rowsPerPage || 10),
      );
    },
    setSearchText: (state, action: PayloadAction<string | undefined>) => {
      state.searchText = action.payload;
      state.pageNumber = 1; // Reset to first page when searching
      const _searchText = action.payload?.toLowerCase() || "";
      let list: any[] = [];
      if (_searchText) {
        list = filterListByKeys(
          state.duplicateActualList,
          _searchText,
          state.columnNames || [],
        );
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        list = [...state.duplicateActualList]; // Reset to original list if search text is empty
      }
      list = sortArray(list, state.sortColumn || "", state.sortOrder || "none");
      state.totalPages = Math.ceil(list.length / (state.rowsPerPage || 10));
      state.actualList = list;
      state.filteredList = list.slice(
        (state.pageNumber - 1) * (state.rowsPerPage || 10),
        state.pageNumber * (state.rowsPerPage || 10),
      );
    },
    setRowsPerPage: (state, action: PayloadAction<number | undefined>) => {
      state.rowsPerPage = action.payload;
      state.pageNumber = 1; // Reset to first page when searching
      const _searchText = state.searchText?.toLowerCase() || "";
      let list: any[] = [];
      if (_searchText) {
        list = filterListByKeys(
          state.duplicateActualList,
          _searchText,
          state.columnNames || [],
        );
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        list = [...state.duplicateActualList]; // Reset to original list if search text is empty
      }
      list = sortArray(list, state.sortColumn || "", state.sortOrder || "none");
      state.totalPages = Math.ceil(list.length / (state.rowsPerPage || 10));
      state.actualList = list;
      state.filteredList = list.slice(
        (state.pageNumber - 1) * (state.rowsPerPage || 10),
        state.pageNumber * (state.rowsPerPage || 10),
      );
    },
    setPageNumber: (state, action: PayloadAction<number | undefined>) => {
      state.pageNumber = action.payload;
      const _searchText = state.searchText?.toLowerCase() || "";
      let list: any[] = [];
      if (_searchText) {
        list = filterListByKeys(
          state.duplicateActualList,
          _searchText,
          state.columnNames || [],
        );
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        list = [...state.duplicateActualList]; // Reset to original list if search text is empty
      }
      list = sortArray(list, state.sortColumn || "", state.sortOrder || "none");
      state.totalPages = Math.ceil(list.length / (state.rowsPerPage || 10));
      state.actualList = list;
      state.filteredList = list.slice(
        ((action.payload ?? 1) - 1) * (state.rowsPerPage || 10),
        (action.payload ?? 1) * (state.rowsPerPage || 10),
      );
    },
    setFilteredList: (state, action: PayloadAction<any[]>) => {
      state.filteredList = action.payload;
      state.pageNumber = 1; // Reset to first page when filtering
      state.totalPages = Math.ceil(
        action.payload.length / (state.rowsPerPage || 10),
      );
    },
    setSortOrder: (state, action: PayloadAction<string>) => {
      state.sortOrder = action.payload;
    },
    setSortColumn: (state, action: PayloadAction<string | null>) => {
      state.sortColumn = action.payload;
    },
    setIsTableLoading: (state, action: PayloadAction<boolean>) => {
      state.isTableLoading = action.payload;
    },
    setColumnNames: (state, action: PayloadAction<any>) => {
      state.columnNames = action.payload;
    },
  },
});

export const {
  setInitialState,
  setActualList,
  setSortState,
  setSearchText,
  setRowsPerPage,
  setPageNumber,
  setSortOrder,
  setSortColumn,
  setIsTableLoading,
  setColumnNames,
  setFilteredList,
} = tableModalSlice.actions;
export default tableModalSlice.reducer;
